import Box from '@fuegokit/react/components/Box'
import sx, {SxProp} from '@fuegokit/react/sx'
import themeGet from '@fuegokit/react/utils/theme-get'
import React, {ComponentProps} from 'react'
import styled, {keyframes} from 'styled-components'

const SIZES = {
  xsmall: '8px',
  small: '16px',
  medium: '24px',
  large: '48px',
  xlarge: '96px'
}

const rotateKeyframes = keyframes`
  100% {
      transform: rotate(360deg);
  } 
`

const Wrapper = styled(Box)`
  animation: ${rotateKeyframes} 0.86s infinite;
  animation-timing-function: cubic-bezier(0.4, 0.15, 0.6, 0.85);
  transform-origin: center;
`
/**
 * There are three parts to the load in animation:
 * 1. Fade in
 * 2. Accelerated spin
 * 3. Stretch the spinner line
 */
const loadIn = keyframes`
 from {
   transform: 'rotate(50deg)';
   opacity: 0;
   stroke-dashoffset: 60
 }
 to {
   transform: 'rotate(230deg)';
   opacity: 1;
   stroke-dashoffset: 50
 }
`
const StyledTracklessSVG = styled.svg`
  animation: ${loadIn} 1s ease-in-out;
  animation-fill-mode: forwards;
  opacity: 0;
`
const Circle = styled.circle<SpinnerInternalProps>`
  fill: none;
  stroke-dasharray: 60;
  stroke-dashoffset: inherit;
  stroke-linecap: round;
  stroke-width: 1.5;

  @media screen and (forced-colors: active) {
    filter: grayscale(100%);
    stroke: CanvasText;
  }
  stroke: ${p => (p.appearance === 'inherit' ? themeGet('colors.text.subtle') : themeGet('colors.text.inverse'))};
`

export type SpinnerAppearance = 'inherit' | 'invert'

export interface SpinnerInternalProps {
  /**
   * Sets the width and height of the spinner.
   */
  size?: keyof typeof SIZES | number
  /**
   * Whether the spinner should invert against a bold background or inherit. This is useful when you are displaying a spinner on a background that is not the same background color scheme as the body
   */
  appearance?: SpinnerAppearance
  /**
   * Delay the intro animation of `<ProgressSpinner/>`. This is not to be used to avoid quick flickering of `<ProgressSpinner/>`. `<ProgressSpinner/>` will automatically fade in and takes ~200ms to become partially visible. This prop can be helpful for **long delays** such as `500-1000ms` for when you want to not show a `<ProgressSpinner/>` until some longer period of time has elapsed.
   */
  delay?: number
  /**
   * A `testId` prop is provided for specified elements, which is a unique string that appears as a data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
   */
  testId?: string
  /**
   * An aria-label must be provided for accessibility. If the Spinner is labeled by a separate element, an aria-labelledby prop must be provided using the id of the labeling element instead.
   */
  ariaLabel?: string
  /**
   * An aria-label must be provided for accessibility. If the Spinner is labeled by a separate element, an aria-labelledby prop must be provided using the id of the labeling element instead.
   */
  ariaLabelledBy?: string
}
const Spinner = ({
  size: sizeKey = 'medium',
  testId,
  delay,
  ariaLabel,
  ariaLabelledBy,
  appearance = 'inherit',
  ...props
}: SpinnerInternalProps) => {
  const size = typeof sizeKey === 'number' ? sizeKey : SIZES[sizeKey]

  if (!ariaLabel && !ariaLabelledBy) {
    console.warn('Please add an aria-label or aria-labelledby attribute to Spinner for accessibility.')
  }

  return (
    <Wrapper
      as="span"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      sx={{
        display: 'inline-flex',
        verticalAlign: 'middle',
        width: size,
        height: size
      }}
    >
      <StyledTracklessSVG height={size} width={size} viewBox="0 0 16 16" data-testid={testId} {...props}>
        <Circle cx="8" cy="8" r="7" appearance={appearance} />
      </StyledTracklessSVG>
    </Wrapper>
  )
}

const StyledSpinner = styled(Spinner)<SxProp>`
  ${sx}
`

StyledSpinner.displayName = 'Spinner'
export type SpinnerProps = ComponentProps<typeof StyledSpinner>
export default StyledSpinner
